// Helper to format phone number with +1
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove any existing +1 and any non-digit characters
    const cleaned = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '');
    // Add +1 prefix
    return `+1${cleaned}`;
};

// Helper to clean phone number for storage (removes all non-digit characters)
export const cleanPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters, including +1 prefix
    return phoneNumber.replace(/\D/g, '');
};

// Helper to format phone number for display (XXX) XXX-XXXX
export const formatPhoneNumberForDisplay = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = cleanPhoneNumber(phoneNumber);
    
    // Only format if we have a valid 10-digit number
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    return phoneNumber; // Return original if not valid 10-digit number
};

// Helper to format phone number for list display with +1 prefix: +1 (XXX) XXX-XXXX
export const formatPhoneNumberForList = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = cleanPhoneNumber(phoneNumber);
    
    // Handle both 10-digit and 11-digit (with country code) formats
    let digitsToFormat = cleaned;
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // Remove the leading '1' country code
        digitsToFormat = cleaned.substring(1);
    }
    
    // Only format if we have a valid 10-digit number
    if (digitsToFormat.length === 10) {
        return `+1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6)}`;
    }
    
    return phoneNumber; // Return original if not valid format
};

// Helper to clean phone number for form input (removes +1 prefix for editing)
export const cleanPhoneNumberForEdit = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove +1 prefix if present, then clean
    let cleaned = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '');
    
    // If it's an 11-digit number starting with 1, remove the leading 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        cleaned = cleaned.substring(1);
    }
    
    return cleaned;
};

// Helper to format phone number for form editing (pre-formatted for UI library)
export const formatPhoneNumberForEdit = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // First clean the phone number
    const cleaned = cleanPhoneNumberForEdit(phoneNumber);
    
    // If we have a valid 10-digit number, format it as (XXX) XXX-XXXX
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    return cleaned; // Return as-is if not 10 digits
};

// Helper to validate US phone number format
export const isValidUSPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return false;
    const cleaned = cleanPhoneNumber(phoneNumber);
    
    // Handle both 10-digit (without country code) and 11-digit (with +1 country code) formats
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // Remove the leading '1' (US country code) and validate the remaining 10 digits
        const withoutCountryCode = cleaned.substring(1);
        return /^[2-9][0-9]{9}$/.test(withoutCountryCode);
    } else if (cleaned.length === 10) {
        // Validate 10-digit format directly
        return /^[2-9][0-9]{9}$/.test(cleaned);
    }
    
    return false;
};

// Helper to clean tax ID for storage (removes all non-digit characters)
export const cleanTaxId = (taxId) => {
    if (!taxId) return '';
    // Remove all non-digit characters, including dashes and spaces
    return taxId.replace(/\D/g, '');
};

// Helper to validate tax ID format (9 digits)
export const isValidTaxId = (taxId) => {
    if (!taxId) return false;
    const cleaned = cleanTaxId(taxId);
    
    // Check if it's exactly 9 digits
    return /^\d{9}$/.test(cleaned);
};

// Helper to format tax ID for display (XX-XXXXXXX)
export const formatTaxIdForDisplay = (taxId) => {
    if (!taxId) return '';
    const cleaned = cleanTaxId(taxId);
    
    // Only format if we have a valid 9-digit tax ID
    if (cleaned.length === 9) {
        return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    }
    
    return taxId; // Return original if not valid 9-digit tax ID
};

// Helper to clean NPI for storage (removes all non-digit characters)
export const cleanNPI = (npi) => {
    if (!npi) return '';
    // Remove all non-digit characters, including dashes and spaces
    return npi.replace(/\D/g, '');
};

// Helper to validate NPI format (10 digits)
export const isValidNPI = (npi) => {
    if (!npi) return false;
    const cleaned = cleanNPI(npi);
    
    // Check if it's exactly 10 digits
    return /^\d{10}$/.test(cleaned);
};

// Helper to format NPI for display (XXXXXXXXXX - keeping as plain digits for now)
export const formatNPIForDisplay = (npi) => {
    if (!npi) return '';
    const cleaned = cleanNPI(npi);
    
    // Only format if we have a valid 10-digit NPI
    if (cleaned.length === 10) {
        return cleaned; // NPIs are typically displayed as plain 10-digit numbers
    }
    
    return npi; // Return original if not valid 10-digit NPI
};